// Phase 15 Plan 05 Task 2 — Per-user PublicProfileResponse cache (60s TTL).
//
// Drives ProfileScreen navigation (Plan 08) — visiting another player's
// profile, navigating away, and back within 60s reads from cache instead
// of re-hitting /api/profile/{userId}.
//
// TTL 60s matches the server-side leaderboard KV TTL.
//
// In-memory only — `map()` (NOT `persistentMap()`). A page reload re-fetches
// every entry through the auth gate. The cache holds whatever the server
// returned; `friend_code` is server-nulled for non-self callers (Plan 04
// enforces this on the wire boundary), so the UI never reads a leaked
// friend code from cache.

import { map } from 'nanostores'
import type { PublicProfileResponse } from '../net/profile'

interface CacheEntry {
  readonly data: PublicProfileResponse
  readonly expires_at: number
}

export const PROFILE_CACHE_TTL_MS = 60_000

export const $profileCache = map<Record<string, CacheEntry | undefined>>({})

export function setCachedProfile(
  userId: string,
  data: PublicProfileResponse,
): void {
  $profileCache.setKey(userId, {
    data,
    expires_at: Date.now() + PROFILE_CACHE_TTL_MS,
  })
}

export function getCachedProfile(userId: string): PublicProfileResponse | null {
  const entry = $profileCache.get()[userId]
  if (!entry) return null
  if (entry.expires_at < Date.now()) return null
  return entry.data
}

/**
 * Remove the cache entry for a given userId. Plan 08's EditNameModal calls
 * this on a successful PATCH /api/account/display-name so the next profile
 * render sees the freshly-renamed self.
 */
export function invalidateProfileCache(userId: string): void {
  // nanostores convention: setKey to undefined removes the entry.
  $profileCache.setKey(userId, undefined)
}
